/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import axios from "axios";
import DashboardShell from "@/components/dashboard/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ShieldCheck,
  ShieldOff,
  Copy,
  Check,
  KeyRound,
  Smartphone,
} from "lucide-react";
import { useAuth, formatApiError } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Security() {
  const { user, refresh } = useAuth();
  const [setup, setSetup] = useState(null); // { qr_code, secret, uri }
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [disableOpen, setDisableOpen] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const startSetup = async () => {
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/auth/2fa/setup`,
        {},
        { withCredentials: true }
      );
      setSetup(data);
      setBackupCodes(null);
      setCode("");
    } catch (err) {
      toast.error("Erreur", {
        description: formatApiError(err?.response?.data?.detail),
      });
    }
  };

  const verifyAndEnable = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/2fa/enable`,
        { code: code.replace(/\s/g, "") },
        { withCredentials: true }
      );
      setBackupCodes(data.backup_codes);
      setSetup(null);
      await refresh();
      toast.success("2FA activée");
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const copySecret = () => {
    if (setup?.secret) {
      navigator.clipboard.writeText(setup.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const downloadBackupCodes = () => {
    const txt = `Mazra3ti — Codes de récupération 2FA pour ${user?.email}\n\nÀ usage unique. Conservez-les en lieu sûr.\n\n${backupCodes.join(
      "\n"
    )}\n`;
    const blob = new Blob([txt], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "mazra3ti-2fa-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardShell allowedRoles={["owner", "trainer"]}>
      <header className="mb-8">
        <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-[#E05A3D]">
          <ShieldCheck size={14} /> Sécurité
        </div>
        <h1 className="mt-2 font-display text-3xl md:text-4xl font-black text-[#1C3A27]">
          Authentification à deux facteurs
        </h1>
        <p className="mt-1 text-[#5C6B5C]">
          Ajoutez une seconde barrière : un code à 6 chiffres généré par une
          application sur votre téléphone à chaque connexion.
        </p>
      </header>

      {/* Status card */}
      <div className="rounded-3xl border border-[#E2E4DF] bg-white p-7 md:p-9 mb-7">
        <div className="flex items-start gap-5">
          <div
            className={`w-14 h-14 rounded-2xl grid place-items-center shrink-0 ${
              user?.totp_enabled
                ? "bg-emerald-100 text-emerald-700"
                : "bg-amber-100 text-amber-700"
            }`}
          >
            {user?.totp_enabled ? (
              <ShieldCheck size={26} />
            ) : (
              <ShieldOff size={26} />
            )}
          </div>
          <div className="flex-1">
            <div className="font-display text-xl font-bold text-[#1C3A27]">
              {user?.totp_enabled
                ? "2FA est activée sur votre compte"
                : "2FA n'est pas encore activée"}
            </div>
            <p className="mt-1 text-[#5C6B5C]">
              {user?.totp_enabled
                ? "Vos connexions sont protégées par un code à 6 chiffres temporaire."
                : "Nous vous recommandons fortement d'activer la 2FA, surtout en Tunisie où le risque de piratage est élevé."}
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              {!user?.totp_enabled && !setup && !backupCodes && (
                <Button
                  data-testid="start-2fa-setup"
                  onClick={startSetup}
                  className="h-11 px-5 bg-[#1C3A27] hover:bg-[#142A1C] text-white rounded-xl font-semibold"
                >
                  <Smartphone size={16} className="me-2" />
                  Activer la 2FA
                </Button>
              )}
              {user?.totp_enabled && (
                <Button
                  data-testid="open-disable-2fa"
                  variant="outline"
                  onClick={() => setDisableOpen(true)}
                  className="h-11 px-5 rounded-xl"
                >
                  Désactiver la 2FA
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Setup wizard */}
      {setup && (
        <div className="rounded-3xl border-2 border-[#E05A3D] bg-white p-7 md:p-9 mb-7">
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#E05A3D] font-bold">
                Étape 1
              </div>
              <h2 className="font-display text-2xl font-bold text-[#1C3A27] mt-1">
                Scannez ce QR code
              </h2>
              <p className="mt-2 text-[#5C6B5C] text-sm">
                Ouvrez{" "}
                <strong>Google Authenticator</strong>, <strong>Authy</strong> ou
                Microsoft Authenticator sur votre téléphone et scannez ce code.
              </p>

              <div className="mt-5 inline-block p-4 rounded-2xl bg-white border border-[#E2E4DF]">
                <img
                  src={setup.qr_code}
                  alt="QR code 2FA"
                  data-testid="qr-image"
                  className="w-56 h-56 block"
                />
              </div>

              <div className="mt-5">
                <div className="text-xs uppercase tracking-widest text-[#5C6B5C] font-semibold">
                  Ou saisissez la clé manuellement
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <code
                    data-testid="totp-secret"
                    className="font-mono text-sm bg-[#F0F0EB] text-[#1C3A27] px-3 py-2 rounded-lg break-all flex-1"
                  >
                    {setup.secret}
                  </code>
                  <Button
                    variant="outline"
                    onClick={copySecret}
                    data-testid="copy-secret"
                    className="shrink-0"
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                  </Button>
                </div>
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest text-[#E05A3D] font-bold">
                Étape 2
              </div>
              <h2 className="font-display text-2xl font-bold text-[#1C3A27] mt-1">
                Entrez le code à 6 chiffres
              </h2>
              <p className="mt-2 text-[#5C6B5C] text-sm">
                Une fois le compte ajouté dans votre app, entrez le code généré
                pour confirmer.
              </p>

              <form
                onSubmit={verifyAndEnable}
                data-testid="enable-2fa-form"
                className="mt-5 space-y-4"
              >
                <Input
                  data-testid="enable-2fa-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  autoFocus
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123 456"
                  className="h-14 bg-white border-[#E2E4DF] rounded-xl text-center text-2xl font-mono tracking-widest focus-visible:ring-[#E05A3D]"
                />

                {error && (
                  <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm p-3">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  data-testid="enable-2fa-submit"
                  disabled={submitting || code.replace(/\s/g, "").length !== 6}
                  className="w-full h-12 rounded-xl bg-[#E05A3D] hover:bg-[#C84B31] text-white font-semibold"
                >
                  {submitting ? "Activation…" : "Activer la 2FA"}
                </Button>

                <button
                  type="button"
                  onClick={() => {
                    setSetup(null);
                    setCode("");
                    setError("");
                  }}
                  className="w-full text-sm text-[#5C6B5C] hover:text-[#1C3A27]"
                >
                  Annuler
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Backup codes shown once */}
      {backupCodes && (
        <div
          data-testid="backup-codes-card"
          className="rounded-3xl border-2 border-amber-500 bg-amber-50 p-7 md:p-9 mb-7"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-500 grid place-items-center shrink-0">
              <KeyRound size={22} className="text-white" />
            </div>
            <div>
              <h3 className="font-display text-xl font-bold text-amber-900">
                Vos codes de récupération — à conserver maintenant
              </h3>
              <p className="mt-1 text-amber-800 text-sm">
                Ces 8 codes vous permettent de vous connecter si vous perdez
                votre téléphone. Chaque code n'est utilisable qu'une seule fois.
                <strong> Ils ne seront plus jamais affichés.</strong>
              </p>
            </div>
          </div>
          <ul className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-2xl">
            {backupCodes.map((c, i) => (
              <li
                key={i}
                data-testid={`backup-code-${i}`}
                className="font-mono text-sm bg-white rounded-lg px-3 py-2 text-center text-amber-900 border border-amber-300"
              >
                {c}
              </li>
            ))}
          </ul>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button
              data-testid="download-backup-codes"
              onClick={downloadBackupCodes}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              Télécharger en .txt
            </Button>
            <Button
              variant="outline"
              onClick={() => setBackupCodes(null)}
              data-testid="dismiss-backup-codes"
            >
              J'ai sauvegardé les codes
            </Button>
          </div>
        </div>
      )}

      <DisableDialog
        open={disableOpen}
        onClose={() => setDisableOpen(false)}
        onDone={refresh}
      />
    </DashboardShell>
  );
}

function DisableDialog({ open, onClose, onDone }) {
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/auth/2fa/disable`,
        { password, code },
        { withCredentials: true }
      );
      toast.success("2FA désactivée");
      setPassword("");
      setCode("");
      onClose();
      await onDone?.();
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            Désactiver la 2FA
          </DialogTitle>
          <DialogDescription>
            Cette action réduit la sécurité de votre compte. Vous devrez la
            réactiver pour bénéficier à nouveau de cette protection.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label>Mot de passe actuel</Label>
            <Input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 h-11 bg-white"
            />
          </div>
          <div>
            <Label>Code 2FA actuel ou code de récupération</Label>
            <Input
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456 ou XXXX-XXXX"
              className="mt-1.5 h-11 bg-white font-mono"
            />
          </div>
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm p-3">
              {error}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {submitting ? "…" : "Désactiver"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
